import { Box, Button, Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { Section } from "./section";
import { SectionViewModel } from "./section.view-model";
import { SectionsReorder } from "./sections-reorder";
import SettingsDescription from "pages/Settings/elements/SettingsDescription";
import SettingsHeader from "pages/Settings/elements/SettingsHeader";
import { useTenant } from "modules/tenant/providers/tenant-provider";

export function Sections() {
  const { user } = useTenant();

  const [, setIsLoading] = useState(true);
  const [addingNewSection, setAddingNewSection] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [sections, setSections] = useState<SectionViewModel[]>([]);

  useEffect(() => {
    const editing = sections.some((s: SectionViewModel) => s.id === "");
    setAddingNewSection(editing);
  }, [sections]);

  useEffect(() => {
    async function loadSections() {
      setIsLoading(true);

      try {
        let { sections } = await apiRequest("/supabase/loan_section/select");

        sections = sections
          .map((section: any) => {
            const fields = user.company.fields
              .filter((field: any) => field.section !== null && field.section.id === section.id)
              .sort((a: any, b: any) => a.order - b.order);

            return {
              ...section,
              fields: fields,
            };
          })
          .sort((a: any, b: any) => a.order - b.order);

        setSections(sections);
      } finally {
        setIsLoading(false);
      }
    }

    loadSections();
  }, [user.company.fields]);

  function handleReOrderSections(sections: SectionViewModel[]) {
    setSections(sections);
    setReordering(false);
  }

  function handleAddNewSection() {
    const newSection: SectionViewModel = {
      id: "",
      section_name: "",
      fields: [],
      order: sections[sections.length - 1].order + 1,
    };

    setSections(sections => [newSection, ...sections]);
  }

  function handleOnDeleteSection(section: SectionViewModel) {
    setSections(sections => sections.filter((s: SectionViewModel) => s.id !== section.id));
  }

  function handleOnUpdateSection(section: SectionViewModel) {
    setSections(sections => sections.map(s => (s.id === section.id ? section : s)));
  }

  function handleOnAddSection(section: SectionViewModel) {
    setSections(sections => [section, ...sections.filter(s => s.id !== "")]);
  }

  return (
    <Flex direction="column">
      <Box>
        <SettingsHeader title="Loan Fields" />
        <SettingsDescription text="Customize the data you collect about loans. All loan fileds added here will be the default field in all loan types." />
      </Box>

      {reordering ? (
        <SectionsReorder
          sections={sections}
          onCancel={() => setReordering(false)}
          onSaveChanges={handleReOrderSections}
        />
      ) : (
        <Flex direction="column" gap={4}>
          <Flex gap={3}>
            {sections.length > 0 && (
              <Button
                variant="outline"
                leftIcon={<FontAwesomeIcon icon={["far", "bars"]} />}
                onClick={() => setReordering(true)}
                disabled={addingNewSection}
              >
                Re-Order Sections
              </Button>
            )}
            <Button
              variant={getCustomButtonVariant(user)}
              leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
              onClick={handleAddNewSection}
              disabled={addingNewSection}
            >
              New Section
            </Button>
          </Flex>
          <Box>
            {sections.map(section => (
              <Section
                key={section.id}
                section={section}
                onDelete={handleOnDeleteSection}
                onUpdate={handleOnUpdateSection}
                onAdd={handleOnAddSection}
              />
            ))}
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
